/**
 * Supabase client — single instance for the whole app.
 *
 * ─── WAAROM DEZE KEY HIER MAG STAAN ─────────────────────────────────────────
 *
 * `SUPABASE_PUBLISHABLE_KEY` is bedoeld om in de browser te staan. Iedereen kan
 * hem uitlezen; dat is geen lek. De beveiliging zit volledig in row-level
 * security op de database (zie `supabase/migrations/0001_secure_foundation.sql`).
 *
 * Wat hier NOOIT mag staan:
 *   - de secret key (`sb_secret_…` / `service_role`) — die omzeilt alle RLS
 *   - het database-wachtwoord of een `postgresql://`-connectionstring
 *
 * Een browser kan überhaupt geen directe Postgres-verbinding openen; alle
 * toegang loopt via de REST/auth-endpoints van Supabase.
 */

import { createClient, type SupabaseClient } from '@supabase/supabase-js'

const SUPABASE_URL = 'https://ghhloppktdcvtfdkywvg.supabase.co'
const SUPABASE_PUBLISHABLE_KEY =
  'sb_publishable_suT0rGmWvY3YwZ7vLJzJXA_XFADrNed'

/**
 * Eén client-instantie voor de hele app.
 *
 * `persistSession` + `autoRefreshToken` zorgen dat een leerling niet halverwege
 * een toets uitgelogd raakt doordat het access-token verloopt — met een sessie
 * van 20+ minuten is dat een reëel scenario.
 *
 * `detectSessionInUrl` staat aan voor de e-mailconfirmatie- en
 * wachtwoordherstel-links.
 */
export const supabase: SupabaseClient = createClient(
  SUPABASE_URL,
  SUPABASE_PUBLISHABLE_KEY,
  {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
      storageKey: 'meester_djay_auth',
    },
    global: {
      headers: { 'x-application-name': 'meester-djay' },
    },
  },
)

/** UUID van de ingelogde gebruiker, of null. */
export async function getCurrentUserId(): Promise<string | null> {
  const { data, error } = await supabase.auth.getUser()
  if (error) return null
  return data.user?.id ?? null
}

/**
 * Rol + profiel van de ingelogde gebruiker.
 *
 * De rol komt uit `public.profiles`, NIET uit JWT-metadata: metadata is door de
 * client beïnvloedbaar, een profielrij niet (zie de `profiles_update_self`
 * policy, die rolwijziging blokkeert).
 */
export interface CurrentProfile {
  id: string
  role: 'learner' | 'parent' | 'teacher' | 'admin'
  displayName: string
  grade: number | null
}

export async function getCurrentProfile(): Promise<CurrentProfile | null> {
  const userId = await getCurrentUserId()
  if (!userId) return null

  const { data, error } = await supabase
    .from('profiles')
    .select('id, role, display_name, grade')
    .eq('id', userId)
    .single()

  if (error || !data) return null

  return {
    id: data.id as string,
    role: data.role as CurrentProfile['role'],
    displayName: data.display_name as string,
    grade: (data.grade as number | null) ?? null,
  }
}
